import "@xyflow/react/dist/style.css";

import DeleteOutline from "@mui/icons-material/DeleteOutlined";
import { Box, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import {
	addEdge,
	applyEdgeChanges,
	applyNodeChanges,
	Background,
	type Connection,
	Controls,
	type EdgeTypes,
	MarkerType,
	type NodeTypes,
	type OnEdgesChange,
	type OnNodesChange,
	ReactFlow,
	type ReactFlowInstance,
	reconnectEdge,
	type XYPosition,
} from "@xyflow/react";
import {
	type ComponentType,
	type MouseEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { LOOP_SUB_CANVAS_MIN_ZOOM, RULE_CANVAS_GRID_SIZE } from "../../canvas/constants";
import type { Option } from "../../constants";
import { DEFAULT_EDGE_COLOR } from "../../edges/BaseEdge";
import { createConnectionLine } from "../../edges/ConnectionLine";
import DecisionFalseEdge from "../../edges/DecisionFalseEdge";
import DecisionTrueEdge from "../../edges/DecisionTrueEdge";
// Sub-canvas edge types
import DefaultEdge from "../../edges/DefaultEdge";
import LoopAfterLastEdge from "../../edges/LoopAfterLastEdge";
import LoopContinueToStartEdge from "../../edges/LoopContinueToStartEdge";
import LoopForEachEdge from "../../edges/LoopForEachEdge";
import AssignmentForm from "../../forms/AssignmentForm";
import ClearReactionForm from "../../forms/ClearReactionForm";
// Form modals
import DecisionForm from "../../forms/DecisionForm";
import FilterForm, { type FilterFormState } from "../../forms/FilterForm";
import LoopForm from "../../forms/LoopForm";
import QueryBuilderForm, { type QueryBuilderFormState } from "../../forms/QueryBuilderForm";
import ReactionForm, { type ReactionFormState } from "../../forms/ReactionForm";
import SortForm, { type SortFormState } from "../../forms/SortForm";
import useFormModals, { type FormModalKey } from "../../hooks/useFormModals";
import type { RuleVariable } from "../../hooks/useRuleVariables";
import type { RuleEdge, RuleEdgeProps, RuleEdgeType } from "../../types/edges";
import type { LoopNodeData, RuleNode } from "../../types/nodes";
import {
	loopCurrentRowAsSingleRowDatabaseVariable,
	resolveIteratedDatabaseMultiFromLoopNodeData,
} from "../../utils/loop-resource-variables";
import {
	getDuplicateNameAmongSameNodeTypeError,
	getDuplicateReactionAssignmentDecisionNameError,
} from "../../utils/node-name-uniqueness";
import {
	type AddNodeMenuContextData,
	AddNodeMenuProvider,
} from "../add-node-menu-context";
import AddNodeMenu from "../AddNodeMenu";
import AssignmentNode from "../AssignmentNode";
import ClearReactionNode from "../ClearReactionNode";
import DecisionNode from "../DecisionNode";
import FilterNode from "../FilterNode";
import { GhostNode } from "../GhostNode";
import { NodeActionsProvider } from "../node-actions-context";
import { buildAssignmentNode, buildClearReactionNode, buildDecisionNode, buildFilterNode, buildGhostNode, buildLoopNode, buildQueryNode, buildReactionNode, buildSortNode, collectTakenDisplayNames, DEFAULT_NODE_OFFSET_Y, DEFAULT_NODE_POSITION, deleteNodePreservingDecisionEdges,getUniqueDefaultName, snapPositionToGrid } from "../nodeFactory";
// Sub-canvas node types
import QueryBuilderNode from "../QueryBuilderNode";
import ReactionNode from "../ReactionNode";
import SortNode from "../SortNode";
import ContinueSentinelNode from "./ContinueSentinelNode";
import AfterLastSentinelNode from "./EndSentinelNode";
import LoopBreadcrumb from "./LoopBreadcrumb";
import type { LoopFormConfig } from "./loopContext";
import LoopNode from "./LoopNode";
import type { LoopStackEntry } from "./loopTypes";
import {
	AFTER_LAST_SENTINEL_ID,
	buildLoopSubCanvasEdgeFromConnection,
	buildSubCanvasEdges,
	buildSubCanvasNodes,
	CONTINUE_SENTINEL_ID,
	createContinueToStartEdge,
	createLoopStartToEndEdge,
	extractPersistableState,
	FOREACH_SENTINEL_ID,
	isSentinelId,
	LOOP_CONTINUE_TO_START_EDGE_ID,
	LOOP_START_TO_END_EDGE_ID,
	prepareLoopSubCanvasInitialGraph,
	refineForEachReturnEdgeType,
	resolveLoopIterateType,
	type SubCanvasEdge,
	type SubCanvasNode,
} from "./loopTypes";
// Sentinel nodes
import ForEachSentinelNode from "./StartSentinelNode";

// ─── Node/Edge type registries for the sub-canvas ────────────────────────────

type SubCanvasNodeComponent = NodeTypes[string];

const SUB_CANVAS_NODE_TYPES = {
	"loop-start-sentinel": ForEachSentinelNode as SubCanvasNodeComponent,
	"loop-continue-sentinel": ContinueSentinelNode as SubCanvasNodeComponent,
	"loop-end-sentinel": AfterLastSentinelNode as SubCanvasNodeComponent,
	reaction: ReactionNode as SubCanvasNodeComponent,
	clear_reaction: ClearReactionNode as SubCanvasNodeComponent,
	ghost: GhostNode as SubCanvasNodeComponent,
	query_builder: QueryBuilderNode as SubCanvasNodeComponent,
	decision: DecisionNode as SubCanvasNodeComponent,
	filter: FilterNode as SubCanvasNodeComponent,
	sort: SortNode as SubCanvasNodeComponent,
	loop: LoopNode as SubCanvasNodeComponent,
	assignment: AssignmentNode as SubCanvasNodeComponent,
} satisfies NodeTypes;

const SUB_CANVAS_EDGE_TYPES: EdgeTypes & Record<RuleEdgeType, ComponentType<RuleEdgeProps>> = {
	default: DefaultEdge,
	"decision-true": DecisionTrueEdge,
	"decision-false": DecisionFalseEdge,
	"loop-start": LoopForEachEdge,
	"loop-end": LoopAfterLastEdge,
	"loop-for-each-return": DefaultEdge,
	"loop-continue-to-start": LoopContinueToStartEdge,
};

const CLICKABLE_NODE_FORM_KEYS: Partial<Record<string, FormModalKey>> = {
	decision: "decision",
	loop: "loop",
	sort: "sort",
	filter: "filter",
	query_builder: "query_builder",
	reaction: "reaction",
	clear_reaction: "clearReaction",
	assignment: "assignment",
};

// ─── Component ────────────────────────────────────────────────────────────────

interface LoopSubCanvasProps {
  loopStack: LoopStackEntry[];
  initialNodes: RuleNode[];
  initialEdges: RuleEdge[];
  loopNodeData: LoopNodeData;
  onSyncToParent: (
    nodes: RuleNode[],
    edges: RuleEdge[],
    forEachPosition?: XYPosition,
    afterLastPosition?: XYPosition,
    continueNodePosX?: number,
    continueNodePosY?: number,
  ) => void;
  onEnterLoop: (loopNodeId: string, loopName: string, loopType?: "variable" | "table") => void;
  onExitTo: (index: number) => void;
  formConfig: LoopFormConfig;
  /** Main-canvas Manager Approval Popup reactions for loop-body decision resource dropdown. */
  managerApprovalReactionResourceOptions: Option[];
}

const LoopSubCanvas = ({
	loopStack,
	initialNodes,
	initialEdges,
	loopNodeData,
	onSyncToParent,
	onEnterLoop,
	onExitTo,
	formConfig,
	managerApprovalReactionResourceOptions,
}: LoopSubCanvasProps) => {
	// Extract early — used in hooks before the full formConfig destructure at the bottom.
	const allLoopNames = formConfig.allLoopNames;
	const allReactionAssignmentDecisionNames = formConfig.allReactionAssignmentDecisionNames;

	const preparedInitial = useMemo(
		() => prepareLoopSubCanvasInitialGraph(initialNodes, initialEdges),
		[initialNodes, initialEdges],
	);

	const loopIterateType = useMemo(
		() => resolveLoopIterateType(loopNodeData),
		[
			loopNodeData.loopType,
			loopNodeData.databaseTableName,
			loopNodeData.screenTableName,
			loopNodeData.tableName,
			loopNodeData.databaseTableVariableName,
			loopNodeData.collectionName,
			loopNodeData.collectionId,
		],
	);

	// ── Sub-canvas state ──────────────────────────────────────────────────────
	const [subNodes, setSubNodes] = useState<SubCanvasNode[]>(() =>
		buildSubCanvasNodes(preparedInitial.nodes, loopNodeData),
	);
	const [subEdges, setSubEdges] = useState<SubCanvasEdge[]>(() =>
		buildSubCanvasEdges(preparedInitial.edges, preparedInitial.nodes, loopIterateType),
	);

	useEffect(() => {
		setSubNodes((prev) =>
			prev.map((n) =>
				n.id === FOREACH_SENTINEL_ID && n.type === "loop-start-sentinel"
					? { ...n, data: { ...n.data, iterateType: loopIterateType } }
					: n,
			),
		);
		setSubEdges((prev) => {
			let next = prev;
			if (!next.some((e) => e.id === LOOP_CONTINUE_TO_START_EDGE_ID)) {
				next = [...next, createContinueToStartEdge()];
			}
			const hasSynth = next.some((e) => e.id === LOOP_START_TO_END_EDGE_ID);
			if (loopIterateType === "variable" && hasSynth) {
				next = next.filter((e) => e.id !== LOOP_START_TO_END_EDGE_ID);
			}
			if (loopIterateType === "table" && !hasSynth) {
				next = [...next, createLoopStartToEndEdge()];
			}
			return next;
		});
	}, [loopIterateType]);
	const subNodesRef = useRef(subNodes);
	const subEdgesRef = useRef(subEdges);
	subNodesRef.current = subNodes;
	subEdgesRef.current = subEdges;
	const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance<SubCanvasNode, SubCanvasEdge> | null>(null);

	const owningLoopId = loopStack[loopStack.length - 1]?.loopNodeId;

	// ── Sync to parent on every change ──────────────────────────────────────
	useEffect(() => {
		const { nodes, edges, forEachPosition, afterLastPosition, continueNodePosX, continueNodePosY } =
      extractPersistableState(subNodes, subEdges, owningLoopId);
		onSyncToParent(nodes, edges, forEachPosition, afterLastPosition, continueNodePosX, continueNodePosY);
	}, [subNodes, subEdges, owningLoopId]);  

	// ── ReactFlow handlers ───────────────────────────────────────────────────
	const onNodesChange: OnNodesChange<SubCanvasNode> = useCallback((changes) => {
		const removeChanges = changes.filter((c): c is { type: "remove"; id: string } => c.type === "remove");
		const otherChanges = changes.filter((c) => c.type !== "remove");
		const removeIds = removeChanges.map((c) => c.id).filter((id) => !isSentinelId(id));
		if (removeIds.length === 0) {
			setSubNodes((ns) => {
				const updated = applyNodeChanges(changes, ns);
				return updated.map((n) => ({
					...n,
					position: snapPositionToGrid(n.position),
				})) as SubCanvasNode[];
			});
			return;
		}

		let userNodes = subNodesRef.current.filter((n): n is RuleNode => !isSentinelId(n.id));
		let nextEdges = subEdgesRef.current;
		for (const id of removeIds) {
			if (!userNodes.some((n) => n.id === id)) continue;
			const result = deleteNodePreservingDecisionEdges(id, userNodes, nextEdges);
			userNodes = result.nodes;
			nextEdges = result.edges;
		}

		const prev = subNodesRef.current;
		const forEachN = prev.find((n) => n.id === FOREACH_SENTINEL_ID);
		const continueN = prev.find((n) => n.id === CONTINUE_SENTINEL_ID);
		const afterLastN = prev.find((n) => n.id === AFTER_LAST_SENTINEL_ID);
		let nextSubNodes: SubCanvasNode[] = [
			...(forEachN ? [forEachN] : []),
			...userNodes,
			...(continueN ? [continueN] : []),
			...(afterLastN ? [afterLastN] : []),
		];

		if (otherChanges.length > 0) {
			nextSubNodes = applyNodeChanges(otherChanges, nextSubNodes).map((n) => ({
				...n,
				position: snapPositionToGrid(n.position),
			})) as SubCanvasNode[];
		} else {
			nextSubNodes = nextSubNodes.map((n) => ({
				...n,
				position: snapPositionToGrid(n.position),
			})) as SubCanvasNode[];
		}

		setSubNodes(nextSubNodes);
		setSubEdges(nextEdges);
	}, []);
	const onEdgesChange: OnEdgesChange<SubCanvasEdge> = useCallback(
		(changes) => setSubEdges((es) => applyEdgeChanges(changes, es)),
		[],
	);

	const onConnect = useCallback((params: Connection) => {
		const edge = buildLoopSubCanvasEdgeFromConnection(params);
		if (!edge) return;
		setSubEdges((es) =>
			addEdge(
				{
					...edge,
					markerEnd: {
						type: MarkerType.Arrow,
						color: DEFAULT_EDGE_COLOR,
						height: 28,
						width: 28,
					},
				},
				es,
			),
		);
	}, []);

	// ── Edge reconnection ────────────────────────────────────────────────────
	const reconnectSuccessful = useRef<boolean>(true);
	const reconnectingEdgeRef = useRef<SubCanvasEdge | null>(null);

	const onReconnectStart = useCallback((_event: unknown, edge: SubCanvasEdge) => {
		reconnectSuccessful.current = false;
		reconnectingEdgeRef.current = edge;
	}, []);

	const onReconnect = useCallback((oldEdge: SubCanvasEdge, newConnection: Connection) => {
		reconnectSuccessful.current = true;
		const oldTargetId = oldEdge.target;
		setSubEdges((eds) =>
			reconnectEdge(oldEdge, newConnection, eds).map((e) => {
				if (e.id !== oldEdge.id) return refineForEachReturnEdgeType(e);
				const rebuilt = buildLoopSubCanvasEdgeFromConnection(newConnection, {
					id: e.id,
					markerEnd: e.markerEnd,
				});
				return rebuilt ?? refineForEachReturnEdgeType(e);
			}),
		);
		setSubNodes((nds) => {
			const oldTarget = nds.find((n) => n.id === oldTargetId);
			if (oldTarget?.type === "ghost") return nds.filter((n) => n.id !== oldTargetId);
			return nds;
		});
	}, []);

	const onReconnectEnd = useCallback(
		(event: unknown, edge: SubCanvasEdge) => {
			reconnectingEdgeRef.current = null;
			if (!reconnectSuccessful.current) {
				const isRetargetableBodyEdge =
					edge.type === "decision-true" ||
					edge.type === "decision-false" ||
					edge.type === "loop-end" ||
					edge.type === "default" ||
					edge.type === "loop-for-each-return";

				if (isRetargetableBodyEdge && reactFlowInstance) {
					const { clientX, clientY } = event as { clientX: number; clientY: number };
					const dropPosition = reactFlowInstance.screenToFlowPosition({ x: clientX, y: clientY });
					const oldTargetId = edge.target;
					const currentNodes = reactFlowInstance.getNodes() as SubCanvasNode[];
					const oldTarget = currentNodes.find((n) => n.id === oldTargetId);
					if (oldTarget?.type === "ghost") {
						setSubNodes((nds) =>
							nds.map((n) => n.id === oldTargetId ? { ...n, position: snapPositionToGrid(dropPosition) } : n),
						);
					} else {
						const ghostNode = buildGhostNode(dropPosition);
						setSubEdges((eds) =>
							eds.map((e) => e.id === edge.id ? { ...e, target: ghostNode.id, targetHandle: null } : e),
						);
						setSubNodes((nds) => [...nds, ghostNode as SubCanvasNode]);
					}
				} else {
					setSubEdges((eds) => eds.filter((e) => e.id !== edge.id));
				}
			}
		},
		[reactFlowInstance],
	);

	const ConnectionLine = useMemo(() => createConnectionLine(reconnectingEdgeRef), []);

	const handleFlowInit = useCallback(
		(instance: ReactFlowInstance<SubCanvasNode, SubCanvasEdge>) => {
			setReactFlowInstance(instance);
		},
		[],
	);

	// ── fitView after mount ──────────────────────────────────────────────────
	useEffect(() => {
		if (reactFlowInstance) {
			const t = setTimeout(() => {
				reactFlowInstance.fitView({
					duration: 300,
					padding: 0.25,
					minZoom: LOOP_SUB_CANVAS_MIN_ZOOM,
				});
			}, 60);
			return () => clearTimeout(t);
		}
	}, [reactFlowInstance]);

	// ── Form modals (instance scoped to sub-canvas nodes, sentinels excluded) ──
	// Filter out sentinel nodes so form modals only see user-added nodes.
	const subRuleNodes = useMemo(
		() => subNodes.filter((n): n is RuleNode => !isSentinelId(n.id)),
		[subNodes],
	);

	const setSubRuleNodes = useCallback(
		(updater: React.SetStateAction<RuleNode[]>) => {
			setSubNodes((prev) => {
				const sentinels = prev.filter((n) => isSentinelId(n.id));
				const userNodes = prev.filter((n): n is RuleNode => !isSentinelId(n.id));
				const next = typeof updater === "function" ? updater(userNodes) : updater;
				// Keep sentinels in their original positions
				return [...sentinels, ...next];
			});
		},
		[],
	);

	const {
		formState,
		openNodeForm,
		closeNodeForm,
		closeFormForNode,
		decisionFormInitialState,
		handleDecisionSave,
		loopFormInitialState,
		handleLoopSave,
		sortFormInitialState,
		handleSortSave,
		sortActiveNodeId,
		filterFormInitialState,
		handleFilterSave,
		filterActiveNodeId,
		queryFormInitialState,
		handleQuerySave,
		queryActiveNodeId,
		decisionActiveNodeId,
		reactionActiveNodeId,
		reactionFormInitialState,
		handleReactionSave,
		patchActiveReactionNodeData,
		assignmentFormInitialState,
		handleAssignmentSave,
		assignmentActiveNodeId,
		clearReactionFormInitialState,
		handleClearReactionSave,
		clearReactionActiveNodeId,
		loopActiveNodeId,
	} = useFormModals({
		nodes: subRuleNodes,
		edges: subEdges,
		setNodes: setSubRuleNodes,
		setEdges: setSubEdges,
	});

	const findNodeInSubCanvas = useCallback(
		(id: string) => (subNodes as RuleNode[]).find((n) => n.id === id),
		[subNodes],
	);

	const takenReactionAssignmentDecisionNamesForDecision = useMemo((): Set<string> => {
		if (!decisionActiveNodeId) return allReactionAssignmentDecisionNames;
		const active = (subNodes as RuleNode[]).find((n) => n.id === decisionActiveNodeId);
		const activeName = typeof active?.data?.name === "string" ? active.data.name.trim().toLowerCase() : null;
		if (!activeName) return allReactionAssignmentDecisionNames;
		const without = new Set(allReactionAssignmentDecisionNames);
		without.delete(activeName);
		return without;
	}, [allReactionAssignmentDecisionNames, decisionActiveNodeId, subNodes]);

	const takenReactionAssignmentDecisionNamesForReaction = useMemo((): Set<string> => {
		if (!reactionActiveNodeId) return allReactionAssignmentDecisionNames;
		const active = (subNodes as RuleNode[]).find((n) => n.id === reactionActiveNodeId);
		const activeName = typeof active?.data?.name === "string" ? active.data.name.trim().toLowerCase() : null;
		if (!activeName) return allReactionAssignmentDecisionNames;
		const without = new Set(allReactionAssignmentDecisionNames);
		without.delete(activeName);
		return without;
	}, [allReactionAssignmentDecisionNames, reactionActiveNodeId, subNodes]);

	const takenReactionAssignmentDecisionNamesForAssignment = useMemo((): Set<string> => {
		if (!assignmentActiveNodeId) return allReactionAssignmentDecisionNames;
		const active = (subNodes as RuleNode[]).find((n) => n.id === assignmentActiveNodeId);
		const activeName = typeof active?.data?.name === "string" ? active.data.name.trim().toLowerCase() : null;
		if (!activeName) return allReactionAssignmentDecisionNames;
		const without = new Set(allReactionAssignmentDecisionNames);
		without.delete(activeName);
		return without;
	}, [allReactionAssignmentDecisionNames, assignmentActiveNodeId, subNodes]);

	// ── Duplicate name validators ────────────────────────────────────────────
	const getDuplicateDecisionNameError = useCallback(
		(name: string) =>
			getDuplicateReactionAssignmentDecisionNameError(
				allReactionAssignmentDecisionNames,
				decisionActiveNodeId,
				findNodeInSubCanvas,
				name,
			),
		[allReactionAssignmentDecisionNames, decisionActiveNodeId, findNodeInSubCanvas],
	);
	// Check for duplicate loop names globally (across all canvas levels) so that
	// no two loops in the entire rule graph can share a name.
	const getDuplicateLoopNameError = useCallback(
		(name: string): string | undefined => {
			const trimmed = name.trim().toLowerCase();
			if (!trimmed) return undefined;
			const active = loopActiveNodeId ? findNodeInSubCanvas(loopActiveNodeId) : undefined;
			const activeName = typeof active?.data?.name === "string" ? active.data.name.trim().toLowerCase() : null;
			if (trimmed === activeName) return undefined;
			if (allLoopNames.has(trimmed)) return "This name is already used by another loop node.";
			return undefined;
		},
		[findNodeInSubCanvas, loopActiveNodeId, allLoopNames],
	);
	// Pass the global set directly — LoopForm's internal check already skips the
	// current node's own name via the initialState.loopName comparison.
	const takenLoopNames = allLoopNames;
	const getDuplicateSortNameError = useCallback(
		(name: string) => getDuplicateNameAmongSameNodeTypeError(subNodes as RuleNode[], "sort", sortActiveNodeId, name),
		[subNodes, sortActiveNodeId],
	);
	const getDuplicateFilterNameError = useCallback(
		(name: string) => getDuplicateNameAmongSameNodeTypeError(subNodes as RuleNode[], "filter", filterActiveNodeId, name),
		[subNodes, filterActiveNodeId],
	);
	const getDuplicateQueryNameError = useCallback(
		(name: string) => getDuplicateNameAmongSameNodeTypeError(subNodes as RuleNode[], "query_builder", queryActiveNodeId, name),
		[subNodes, queryActiveNodeId],
	);
	const getDuplicateReactionNameError = useCallback(
		(name: string) =>
			getDuplicateReactionAssignmentDecisionNameError(
				allReactionAssignmentDecisionNames,
				reactionActiveNodeId,
				findNodeInSubCanvas,
				name,
			),
		[allReactionAssignmentDecisionNames, reactionActiveNodeId, findNodeInSubCanvas],
	);
	const getDuplicateAssignmentNameError = useCallback(
		(name: string) =>
			getDuplicateReactionAssignmentDecisionNameError(
				allReactionAssignmentDecisionNames,
				assignmentActiveNodeId,
				findNodeInSubCanvas,
				name,
			),
		[allReactionAssignmentDecisionNames, assignmentActiveNodeId, findNodeInSubCanvas],
	);
	const getDuplicateClearReactionNameError = useCallback(
		(name: string) => getDuplicateNameAmongSameNodeTypeError(subNodes as RuleNode[], "clear_reaction", clearReactionActiveNodeId, name),
		[subNodes, clearReactionActiveNodeId],
	);

	// ── Form save wrappers (no variable side-effects inside sub-canvas) ──────
	const handleSortSaveWithVariable = useCallback((state: SortFormState) => {
		handleSortSave(state);
	}, [handleSortSave]);

	const handleFilterSaveWithVariable = useCallback((state: FilterFormState) => {
		handleFilterSave(state);
	}, [handleFilterSave]);

	const handleQuerySaveWithVariable = useCallback((state: QueryBuilderFormState) => {
		handleQuerySave(state);
	}, [handleQuerySave]);

	const handleReactionSaveWithVariable = useCallback((state: ReactionFormState) => {
		handleReactionSave(state);
	}, [handleReactionSave]);

	// ── Node click → open form ───────────────────────────────────────────────
	const handleNodeClick = useCallback(
		(_event: MouseEvent, node: SubCanvasNode) => {
			const formKey = CLICKABLE_NODE_FORM_KEYS[node.type as string];
			if (formKey) {
				openNodeForm(formKey, node.id);
			}
		},
		[openNodeForm],
	);

	// ── Node context menu ────────────────────────────────────────────────────
	const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    nodeId: string;
  } | null>(null);
	const [contextMenuOpen, setContextMenuOpen] = useState(false);

	const handleNodeContextMenu = useCallback(
		(event: MouseEvent, node: SubCanvasNode) => {
			if (isSentinelId(node.id)) return;
			event.preventDefault();
			setContextMenu({ mouseX: event.clientX, mouseY: event.clientY, nodeId: node.id });
			setContextMenuOpen(true);
		},
		[],
	);

	const handleContextMenuClose = useCallback(() => {
		setContextMenuOpen(false);
	}, []);

	const handleContextMenuExited = useCallback(() => {
		setContextMenu(null);
	}, []);

	const handleContextMenuDelete = useCallback(() => {
		if (!contextMenu) return;
		const { nodeId } = contextMenu;
		const userNodes = subNodes.filter((n): n is RuleNode => !isSentinelId(n.id));
		const { nodes: updatedNodes, edges: updatedEdges } = deleteNodePreservingDecisionEdges(nodeId, userNodes, subEdges);
		setSubRuleNodes(updatedNodes);
		setSubEdges(updatedEdges);
		setContextMenuOpen(false);
	}, [contextMenu, subNodes, subEdges, setSubRuleNodes, setSubEdges]);

	// Inline node delete (top-right hover trash on each node tile). Mirrors
	// the main-canvas behavior so loop-scoped nodes get the same affordance.
	const handleInlineNodeDelete = useCallback((nodeId: string) => {
		const userNodes = subNodes.filter((n): n is RuleNode => !isSentinelId(n.id));
		const { nodes: updatedNodes, edges: updatedEdges } = deleteNodePreservingDecisionEdges(nodeId, userNodes, subEdges);
		setSubRuleNodes(updatedNodes);
		setSubEdges(updatedEdges);
		// Close + clear the left-panel form if it belonged to the deleted node.
		closeFormForNode(nodeId);
	}, [subNodes, subEdges, setSubRuleNodes, setSubEdges, closeFormForNode]);

	const subNodeActionsValue = useMemo(
		() => ({ onDeleteNode: handleInlineNodeDelete }),
		[handleInlineNodeDelete]
	);

	// ── AddNodeMenu position helper ──────────────────────────────────────────
	const getAnchoredMenuPosition = useCallback(
		(anchorEl: HTMLElement | null): XYPosition | null => {
			if (!anchorEl || !reactFlowInstance) return null;
			const rect = anchorEl.getBoundingClientRect();
			return snapPositionToGrid(
				reactFlowInstance.screenToFlowPosition({
					x: rect.left + rect.width / 2,
					y: rect.bottom + RULE_CANVAS_GRID_SIZE * 2,
				}),
			);
		},
		[reactFlowInstance],
	);

	// ── Node menu selection (add nodes to sub-canvas) ────────────────────────
	const handleNodeMenuSelect = useCallback(
		(itemKey: string, context: AddNodeMenuContextData) => {
			const anchorPosition = getAnchoredMenuPosition(context.anchorEl);
			let newNodeId: string | null = null;

			const NODE_WIDTH = 250;
			const insertTargetId = context.targetNodeId;
			const insertEdgeId = context.edgeId;
			let isGhostTarget = false;
			let isRealInsertion = false;

			const resolvePosition = (currentNodes: SubCanvasNode[]) => {
				const sourceNode = context.sourceNodeId
					? currentNodes.find((n) => n.id === context.sourceNodeId)
					: undefined;

				let position: XYPosition | undefined;
				let filteredNodes = currentNodes;

				if (insertTargetId) {
					const ghostNode = currentNodes.find(
						(n) => n.id === insertTargetId && n.type === "ghost",
					);
					if (ghostNode) {
						isGhostTarget = true;
						position = snapPositionToGrid({
							x: ghostNode.position.x - NODE_WIDTH / 2,
							y: ghostNode.position.y,
						});
						filteredNodes = currentNodes.filter((n) => n.id !== insertTargetId);
					} else {
						isRealInsertion = true;
						const targetNode = currentNodes.find((n) => n.id === insertTargetId);
						if (sourceNode && targetNode) {
							position = snapPositionToGrid({
								x: (sourceNode.position.x + targetNode.position.x) / 2,
								y: (sourceNode.position.y + targetNode.position.y) / 2,
							});
						}
					}
				}

				if (!position && sourceNode) {
					const yPosition = anchorPosition?.y ?? sourceNode.position.y + DEFAULT_NODE_OFFSET_Y;
					position = snapPositionToGrid({ x: sourceNode.position.x, y: yPosition });
				}

				if (!position) {
					position = anchorPosition ?? DEFAULT_NODE_POSITION;
				}

				return { position, filteredNodes };
			};

			const removeOldEdge = () => {
				if (insertEdgeId) {
					setSubEdges((es) => es.filter((e) => e.id !== insertEdgeId));
				}
			};

			// Decision: wire incoming edge; true continues the chain (or insert target); false gets a placeholder ghost.
			if (itemKey === "decision") {
				let falseGhostId: string | null = null;

				setSubNodes((currentNodes) => {
					const { position, filteredNodes } = resolvePosition(currentNodes);
					const name = getUniqueDefaultName("Decision", allReactionAssignmentDecisionNames, true);
					const newNode = buildDecisionNode(position, undefined, name);
					newNodeId = newNode.id;

					const nodeCenterX = position.x + NODE_WIDTH / 2;
					const ghostOffsetX = RULE_CANVAS_GRID_SIZE * 8;
					const ghostOffsetY = DEFAULT_NODE_OFFSET_Y + NODE_WIDTH / 2;

					const falseGhost = buildGhostNode({
						x: nodeCenterX + ghostOffsetX,
						y: position.y + ghostOffsetY,
					});
					falseGhostId = falseGhost.id;

					return [...filteredNodes, newNode, falseGhost];
				});

				removeOldEdge();

				if (context.sourceNodeId && newNodeId) {
					onConnect({ source: context.sourceNodeId, sourceHandle: context.sourceHandleId ?? null, target: newNodeId, targetHandle: "in" });
				}
				if (newNodeId) {
					if (isRealInsertion && insertTargetId) {
						onConnect({ source: newNodeId, sourceHandle: "true", target: insertTargetId, targetHandle: "in" });
					} else {
						onConnect({
							source: newNodeId,
							sourceHandle: "true",
							target: CONTINUE_SENTINEL_ID,
							targetHandle: "in",
						});
					}
				}
				if (newNodeId && falseGhostId) {
					onConnect({ source: newNodeId, sourceHandle: "false", target: falseGhostId, targetHandle: null });
				}
				if (newNodeId) {
					const relatedNodeIds: string[] = [];
					if (falseGhostId) relatedNodeIds.push(falseGhostId);
					openNodeForm("decision", newNodeId, { isNew: true, relatedNodeIds });
				}
				return;
			}

			// All other non-stop nodes
			const BUILDERS: Partial<Record<string, { build: (pos: XYPosition, id?: string, name?: string) => RuleNode; defaultName: string; formKey?: FormModalKey }>> = {
				loop: { build: buildLoopNode, defaultName: "Loop", formKey: "loop" },
				sort: { build: buildSortNode, defaultName: "Sort", formKey: "sort" },
				filter: { build: buildFilterNode, defaultName: "Filter", formKey: "filter" },
				query_builder: { build: buildQueryNode, defaultName: "Query Builder", formKey: "query_builder" },
				reaction: { build: buildReactionNode, defaultName: "Reaction", formKey: "reaction" },
				clear_reaction: { build: buildClearReactionNode, defaultName: "Clear Reaction", formKey: "clearReaction" },
				assignment: { build: buildAssignmentNode, defaultName: "Assignment", formKey: "assignment" },
			};

			const cfg = BUILDERS[itemKey];
			if (!cfg) return;

			setSubNodes((currentNodes) => {
				const { position, filteredNodes } = resolvePosition(currentNodes);
				// For loop nodes use the globally-scoped name set so that no two loops
				// at any canvas depth can receive the same default name.
				const isLoop = itemKey === "loop";
				const isRas = itemKey === "reaction" || itemKey === "assignment";
				const taken = isLoop ? allLoopNames : isRas ? allReactionAssignmentDecisionNames : collectTakenDisplayNames(filteredNodes as RuleNode[]);
				const name = getUniqueDefaultName(cfg.defaultName, taken, isLoop || isRas);
				const firstRt = formConfig.reactionTypeOptions[0];
				const newNode =
          itemKey === "reaction"
          	? buildReactionNode(
          		position,
          		undefined,
          		name,
          		firstRt?.reactionTypeApiName
          			? {
          				reactionTypeId: firstRt.value,
          				reactionTypeName: firstRt.reactionTypeApiName,
          				reactionTypeFriendlyName: firstRt.label,
          			}
          			: undefined,
          	)
          	: cfg.build(position, undefined, name);
				newNodeId = newNode.id;
				return [...filteredNodes, newNode];
			});

			removeOldEdge();

			if (context.sourceNodeId && newNodeId) {
				onConnect({ source: context.sourceNodeId, sourceHandle: context.sourceHandleId ?? null, target: newNodeId, targetHandle: "in" });
			}
			if (isRealInsertion && newNodeId && insertTargetId) {
				onConnect({ source: newNodeId, sourceHandle: "out", target: insertTargetId, targetHandle: "in" });
			}
			if (cfg.formKey && newNodeId) {
				openNodeForm(cfg.formKey, newNodeId, { isNew: true });
			}
		},
		[getAnchoredMenuPosition, allLoopNames, allReactionAssignmentDecisionNames, formConfig.reactionTypeOptions, onConnect, openNodeForm, setSubEdges],
	);

	// ── Destructure form config ──────────────────────────────────────────────
	const {
		platformId,
		comparativeOperators,
		comparativeOperatorOptions,
		assignmentOperatorOptions,
		globalConstantOptions,
		formulaOperatorOptions,
		expressionFunctionOptions,
		reactionTypeOptions,
		ruleFieldOptions,
		advancedTriggerOptions,
		collectionOptions,
		screenLocationOptions,
		ruleScreenLocationId,
		resourceVariableGroups,
		formulaVariableOptions,
		singleRowDatabaseVariableOptions,
		databaseMultiRow,
		databaseSingleRow,
		simple,
		formula,
		reaction,
		allVariables,
		reactionVariableOptions,
		addSimpleVariable,
	} = formConfig;

	const loopResourceMultiRowPool = useMemo(
		() => allVariables.filter((v) => v.type === "databaseMulti"),
		[allVariables],
	);

	const loopIteratedDatabaseMultiForForms = useMemo(
		() =>
			resolveIteratedDatabaseMultiFromLoopNodeData(
				loopNodeData,
				loopResourceMultiRowPool.length > 0 ? loopResourceMultiRowPool : databaseMultiRow,
			),
		[loopNodeData, loopResourceMultiRowPool, databaseMultiRow],
	);

	const ruleDatabaseSingleRowForLoopForms = useMemo(() => {
		if (!loopIteratedDatabaseMultiForForms) return databaseSingleRow;
		const row = loopCurrentRowAsSingleRowDatabaseVariable(loopIteratedDatabaseMultiForForms);
		return [row, ...databaseSingleRow.filter((v) => v.id !== row.id)];
	}, [loopIteratedDatabaseMultiForForms, databaseSingleRow]);

	const noMultiRowForLoopForms = useMemo<RuleVariable[]>(() => [], []);

	return (
		<Box
			sx={{
				position: "absolute",
				inset: 0,
				display: "flex",
				flexDirection: "column",
				bgcolor: "background.default",
				zIndex: 10,
			}}
		>
			{/* Breadcrumb navigation bar */}
			<LoopBreadcrumb loopStack={loopStack} onNavigate={onExitTo} />

			{/* Isolated ReactFlow canvas */}
			<Box sx={{
				flex: 1,
				position: "relative",
				minHeight: 0,
				// Hide the default dark target-handle dot (top "in" handles); keep it
				// positioned so edges still anchor. Mirrors canvas.styled.tsx.
				"& .react-flow__handle-top": {
					transform: "translate(-50%, -50%)",
					background: "transparent",
					border: "none",
					boxShadow: "none",
				},
				"& .react-flow__handle-bottom": { transform: "translate(-50%, -50%)" },
				// Edges match the MAIN canvas exactly (same teal, dash, speed, and
				// the rounded solid-teal arrowhead) so the loop reads as the same
				// system. Mirrors canvas.styled.tsx.
				"@keyframes coappMarchingAnts": {
					from: { strokeDashoffset: 22 },
					to: { strokeDashoffset: 0 },
				},
				"& .react-flow__edge-path": {
					stroke: "#02D5AB",
					strokeWidth: 2,
					strokeLinecap: "round",
					strokeLinejoin: "round",
					strokeDasharray: "3 7",
					animation: "coappMarchingAnts 1.4s linear infinite",
				},
				"& .react-flow__arrowhead polyline, & .react-flow__arrowhead path": {
					stroke: "#02D5AB",
					strokeWidth: 2,
					strokeLinejoin: "round",
					strokeLinecap: "round",
					fill: "#02D5AB",
				},
			}}>
				<AddNodeMenuProvider onItemSelect={handleNodeMenuSelect}>
					<NodeActionsProvider value={subNodeActionsValue}>
						<ReactFlow<SubCanvasNode, SubCanvasEdge>
							nodes={subNodes}
							edges={subEdges}
							nodeTypes={SUB_CANVAS_NODE_TYPES}
							edgeTypes={SUB_CANVAS_EDGE_TYPES as EdgeTypes}
							onNodesChange={onNodesChange}
							onEdgesChange={onEdgesChange}
							onConnect={onConnect}
							onInit={handleFlowInit}
							onNodeClick={handleNodeClick}
							onNodeContextMenu={handleNodeContextMenu}
							onReconnect={onReconnect}
							onReconnectStart={onReconnectStart}
							onReconnectEnd={onReconnectEnd}
							connectionLineComponent={ConnectionLine}
							edgesReconnectable
							fitView
							minZoom={LOOP_SUB_CANVAS_MIN_ZOOM}
							nodesDraggable
							snapToGrid
							snapGrid={[RULE_CANVAS_GRID_SIZE, RULE_CANVAS_GRID_SIZE]}
							proOptions={{ hideAttribution: true }}
							deleteKeyCode={["Backspace", "Delete"]}
						>
							<Background gap={RULE_CANVAS_GRID_SIZE} color="#D8F5F4" size={3} />
							<Controls />
						</ReactFlow>
						<AddNodeMenu excludeTypes={["stop", "event_listener", "action"]} />
					</NodeActionsProvider>
				</AddNodeMenuProvider>
			</Box>

			{/* Node context menu */}
			<Menu
				open={contextMenuOpen}
				onClose={handleContextMenuClose}
				anchorReference="anchorPosition"
				anchorPosition={
					contextMenu != null
						? { top: contextMenu.mouseY, left: contextMenu.mouseX }
						: undefined
				}
				slotProps={{
					list: { dense: true },
					transition: { onExited: handleContextMenuExited },
				}}
			>
				<MenuItem onClick={handleContextMenuDelete}>
					<ListItemIcon>
						<DeleteOutline fontSize="small" color="error" />
					</ListItemIcon>
					<ListItemText primary="Delete node" />
				</MenuItem>
			</Menu>

			{/* Form modals — scoped to sub-canvas state */}
			<DecisionForm
				open={formState.decision}
				onClose={() => closeNodeForm("decision")}
				onSave={handleDecisionSave}
				initialState={decisionFormInitialState}
				platformId={platformId}
				operatorOptions={comparativeOperatorOptions}
				resourceVariableGroups={resourceVariableGroups}
				formulaVariableOptions={formulaVariableOptions}
				singleRowDatabaseVariableOptions={singleRowDatabaseVariableOptions}
				resourceScreenFields={ruleFieldOptions}
				resourceAdvancedTriggers={advancedTriggerOptions}
				resourceCollections={collectionOptions}
				globalConstantOptions={globalConstantOptions}
				suppressComparativeOperatorsAutoFetch
				getDuplicateNameError={getDuplicateDecisionNameError}
				takenReactionAssignmentDecisionNames={takenReactionAssignmentDecisionNamesForDecision}
				ruleSimpleVariables={simple}
				ruleFormulaVariables={formula}
				ruleDatabaseSingleRowVariables={ruleDatabaseSingleRowForLoopForms}
				ruleDatabaseMultiRowVariables={noMultiRowForLoopForms}
				isLoopContext
				loopCollectionId={loopNodeData.collectionId}
				loopCollectionName={loopNodeData.collectionName}
				loopIterateType={loopIterateType}
				loopScreenTableId={loopNodeData.screenTableId}
				loopScreenTableName={
					loopNodeData.screenTableId != null && String(loopNodeData.screenTableId).trim() !== ""
						? (loopNodeData.screenTableName ?? loopNodeData.tableName)
						: loopNodeData.screenTableName
				}
				managerApprovalReactionResourceOptions={managerApprovalReactionResourceOptions}
				screenLocationOptions={screenLocationOptions}
				ruleScreenLocationId={ruleScreenLocationId}
			/>
			<LoopForm
				open={formState.loop}
				onClose={() => closeNodeForm("loop")}
				onSave={handleLoopSave}
				initialState={loopFormInitialState}
				platformId={platformId ?? ""}
				getDuplicateNameError={getDuplicateLoopNameError}
				takenLoopNames={takenLoopNames}
				ruleDatabaseMultiRowVariables={databaseMultiRow}
				ruleScreenLocationId={ruleScreenLocationId}
				collectionOptions={collectionOptions}
			/>
			<SortForm
				open={formState.sort}
				onClose={() => closeNodeForm("sort")}
				onSave={handleSortSaveWithVariable}
				initialState={sortFormInitialState}
				platformId={platformId ?? ""}
				getDuplicateNameError={getDuplicateSortNameError}
				ruleDatabaseMultiRowVariables={databaseMultiRow}
			/>
			<FilterForm
				open={formState.filter}
				onClose={() => closeNodeForm("filter")}
				onSave={handleFilterSaveWithVariable}
				initialState={filterFormInitialState}
				platformId={platformId ?? ""}
				comparativeOperators={comparativeOperators}
				resourceVariableGroups={resourceVariableGroups}
				formulaVariableOptions={formulaVariableOptions}
				singleRowDatabaseVariableOptions={singleRowDatabaseVariableOptions}
				resourceScreenFields={ruleFieldOptions}
				globalConstantOptions={globalConstantOptions}
				suppressComparativeOperatorsAutoFetch
				getDuplicateNameError={getDuplicateFilterNameError}
				ruleSimpleVariables={simple}
				ruleFormulaVariables={formula}
				ruleDatabaseSingleRowVariables={databaseSingleRow}
				ruleDatabaseMultiRowVariables={databaseMultiRow}
			/>
			<QueryBuilderForm
				open={formState.query_builder}
				onClose={() => closeNodeForm("query_builder")}
				onSave={handleQuerySaveWithVariable}
				initialState={queryFormInitialState}
				platformId={platformId ?? ""}
				operatorOptions={comparativeOperatorOptions}
				suppressComparativeOperatorsAutoFetch
				getDuplicateNameError={getDuplicateQueryNameError}
			/>
			<ReactionForm
				open={formState.reaction}
				onClose={() => closeNodeForm("reaction")}
				onSave={handleReactionSaveWithVariable}
				initialState={reactionFormInitialState}
				onReactionDraftPatch={patchActiveReactionNodeData}
				reactionTypeOptions={reactionTypeOptions}
				patientFieldOptions={ruleFieldOptions}
				platformId={platformId}
				resourceVariableGroups={resourceVariableGroups}
				formulaVariableOptions={formulaVariableOptions}
				singleRowDatabaseVariableOptions={singleRowDatabaseVariableOptions}
				resourceScreenFields={ruleFieldOptions}
				resourceAdvancedTriggers={advancedTriggerOptions}
				screenLocationOptions={screenLocationOptions}
				ruleScreenLocationId={ruleScreenLocationId}
				getDuplicateNameError={getDuplicateReactionNameError}
				takenReactionAssignmentDecisionNames={takenReactionAssignmentDecisionNamesForReaction}
			/>
			<AssignmentForm
				open={formState.assignment}
				onClose={() => closeNodeForm("assignment")}
				onSave={handleAssignmentSave}
				initialState={assignmentFormInitialState}
				platformId={platformId}
				simpleVariables={simple}
				existingVariables={allVariables}
				onAddSimpleVariable={addSimpleVariable}
				operatorOptions={assignmentOperatorOptions}
				suppressAssignmentOperatorsAutoFetch
				getDuplicateNameError={getDuplicateAssignmentNameError}
				takenReactionAssignmentDecisionNames={takenReactionAssignmentDecisionNamesForAssignment}
				resourceVariableGroups={resourceVariableGroups}
				formulaVariableOptions={formulaVariableOptions}
				resourceScreenFields={ruleFieldOptions}
				globalConstantOptions={globalConstantOptions}
				ruleSimpleVariables={simple}
				ruleFormulaVariables={formula}
				ruleDatabaseSingleRowVariables={ruleDatabaseSingleRowForLoopForms}
				ruleDatabaseMultiRowVariables={noMultiRowForLoopForms}
			/>
			<ClearReactionForm
				open={formState.clearReaction}
				onClose={() => closeNodeForm("clearReaction")}
				onSave={handleClearReactionSave}
				initialState={clearReactionFormInitialState}
				reactionOptions={reactionVariableOptions}
				getDuplicateNameError={getDuplicateClearReactionNameError}
			/>
		</Box>
	);
};

export default LoopSubCanvas;
